import { t } from "@lingui/macro";
import { type ResumeData, resumeDataSchema } from "@reactive-resume/schema";

import { axios } from "@/client/libs/axios";

const POLL_INTERVAL_MS = 2000;
// 60s window — large PDFs + AI cold-start can take 20-40s. The old 10×2s=20s
// window timed out legitimate parses and surfaced a misleading "try again"
// error on the onboarding upload step.
const MAX_POLL_ATTEMPTS = 30;

/**
 * Stable error codes the server returns alongside `status: "failed"`. Used
 * by callers to decide whether to retry, suggest a different file, or escalate
 * to support. Mirrors `ResumeParseErrorCode` on the server (kept as a union
 * literal here so we don't pull a server type into the client bundle).
 */
export type ResumeParseErrorCode =
  | "RATE_LIMITED"
  | "AI_PARSE_FAILED"
  | "EXTRACT_FAILED"
  | "LINKEDIN_BLOCKED"
  | "UPSTREAM_UNAVAILABLE"
  | "UPLOAD_FAILED"
  | "TEXT_TOO_LONG"
  | "REQUEST_TOO_LARGE"
  | "INVALID_FILE_TYPE"
  | "FILE_TOO_LARGE"
  | "INTERNAL";

export class ResumeParseError extends Error {
  readonly code: ResumeParseErrorCode;
  readonly retryable: boolean;
  /** Non-PII failure category for analytics (never the file name or contents). */
  readonly reason?: string;
  /**
   * Finer non-PII cause inside `reason` / `code`: why the server could not read a
   * PDF (`password`, `corrupt`, ...), or how long an upload ran before it failed.
   * Drives the message shown and the `detail` analytics prop.
   */
  readonly detail?: string;

  constructor(message: string, code: ResumeParseErrorCode, reason?: string, detail?: string) {
    super(message);
    // eslint-disable-next-line lingui/no-unlocalized-strings -- class identifier, not user-facing
    this.name = "ResumeParseError";
    this.code = code;
    this.reason = reason;
    this.detail = detail;
    this.retryable =
      code === "RATE_LIMITED" ||
      code === "INTERNAL" ||
      code === "UPSTREAM_UNAVAILABLE" ||
      code === "UPLOAD_FAILED";
  }
}

type ParseJobStatus =
  | { status: "pending" }
  | { status: "completed"; data: ResumeData; truncated?: boolean }
  | { status: "failed"; error: string; code?: ResumeParseErrorCode };

/**
 * These endpoints sit behind `TwoFactorGuard`, so they MUST go through the
 * shared `axios` instance rather than raw `fetch`: only that instance carries
 * the axios-auth-refresh interceptor. Called with `fetch`, an upload attempted
 * after the 15-minute access token expired 401'd with no recovery — the user
 * saw a bare failure while holding a Refresh cookie good for 30 days.
 */

/**
 * Pull the server's own error text off a rejected axios call.
 *
 * The response interceptor in `libs/axios.ts` collapses `message` to the
 * literal "ValidationError" whenever the server sends class-validator's
 * `string[]`, so read the raw payload off `error.response` — which that
 * interceptor deliberately preserves — instead of trusting `error.message`.
 */
const serverMessage = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { message?: string | string[] } } }).response
    ?.data?.message;

  return Array.isArray(message) ? message.join(" ") : message;
};

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

const serverCode = (error: unknown): string | undefined => {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;

  return typeof code === "string" ? code : undefined;
};

/**
 * Why the server could not read a PDF (`reason` on its 422 body). Anything outside
 * this list is dropped, so the value that reaches the UI and analytics is always
 * one of these.
 */
const PDF_UNREADABLE_REASONS: ReadonlySet<string> = new Set([
  "no_text_layer",
  "password",
  "corrupt",
  "parse_error",
]);

const serverReason = (error: unknown): string | undefined => {
  const reason = (error as { response?: { data?: { reason?: unknown } } } | null)?.response?.data
    ?.reason;

  return typeof reason === "string" && PDF_UNREADABLE_REASONS.has(reason) ? reason : undefined;
};

/** HTTP status the server uses for a file it cannot extract text from. */
const HTTP_UNPROCESSABLE = 422;

/** HTTP status the server uses when the daily parse quota is spent. */
const HTTP_TOO_MANY_REQUESTS = 429;

/** HTTP status that means "the file is bigger than the proxy/server accepts". */
const HTTP_PAYLOAD_TOO_LARGE = 413;

/**
 * Short, non-PII category for a failed import, for the `onboarding_import_failed`
 * analytics event: a `ResumeParseError.reason`, `http_<status>` when the server
 * answered, `network` when an axios request got no answer at all, else the
 * error's class name (`TypeError`, `RangeError`, ...). Never the message, which
 * can echo file content, and never the file name.
 */
export const classifyImportFailure = (error: unknown): string => {
  const reason = (error as { reason?: unknown } | null)?.reason;
  if (typeof reason === "string" && reason) return reason;

  const status = statusOf(error);
  if (status !== undefined) return `http_${status}`;
  if ((error as { isAxiosError?: boolean } | null)?.isAxiosError) return "network";

  const name = (error as { name?: unknown } | null)?.name;
  return typeof name === "string" && /^[A-Za-z]{1,40}$/.test(name) ? name : "unknown";
};

const uploadFailureMessage = (error: unknown): string => {
  const fromServer = serverMessage(error);
  if (fromServer) return fromServer;

  const status = statusOf(error);
  if (status === HTTP_PAYLOAD_TOO_LARGE) {
    return t`That file is too large to upload. Please choose a smaller PDF or DOCX.`;
  }
  return status === undefined
    ? t`Upload failed. Please check your connection and try again.`
    : t`Upload failed (HTTP ${status}).`;
};

/** True for an axios failure with no HTTP answer (not a cancel, not a server status). */
const isNoResponseError = (error: unknown): boolean => {
  const axiosError = error as { isAxiosError?: boolean; code?: string } | null;
  return (
    axiosError?.isAxiosError === true &&
    statusOf(error) === undefined &&
    axiosError.code !== "ERR_CANCELED"
  );
};

/** Buckets for how long the upload ran before failing without an answer. */
const UPLOAD_FAIL_FAST_MS = 5000;
const UPLOAD_FAIL_SLOW_MS = 30_000;

/**
 * `no_answer_<bucket>`: low-cardinality, non-PII, tells a connection reset (fast)
 * from a timeout or proxy cut (slow). Not retried automatically: the server charges
 * the daily slot and queues the job before it answers, so a blind re-send after a
 * lost response could spend a second slot for one import.
 */
const networkFailureDetail = (elapsedMs: number): string => {
  let bucket = "gt30s";
  if (elapsedMs < UPLOAD_FAIL_FAST_MS) bucket = "lt5s";
  else if (elapsedMs < UPLOAD_FAIL_SLOW_MS) bucket = "5_to_30s";
  return `no_answer_${bucket}`;
};

/** Request config for parse calls whose errors the caller renders inline. */
const inlineErrors = (suppressErrorToast: boolean | undefined) =>
  suppressErrorToast ? { suppressErrorToast: true } : undefined;

/** Callbacks and flags shared by every server-parse entry point. */
export type PollParseOptions = {
  suppressErrorToast?: boolean;
  /**
   * Called when the server could only read the first part of the CV, so the
   * caller can tell the user to check the later sections (never a silent loss).
   */
  onTruncated?: () => void;
};

export async function pollParseJob(
  jobId: string,
  options: PollParseOptions = {},
): Promise<ResumeData> {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    let result: ParseJobStatus;

    try {
      const pollResponse = await axios.get<ParseJobStatus>(
        `/resume-parser/parse/${jobId}`,
        inlineErrors(options.suppressErrorToast),
      );
      result = pollResponse.data;
    } catch (error) {
      throw new ResumeParseError(
        t`Failed to retrieve parse result.`,
        "INTERNAL",
        `poll_${classifyImportFailure(error)}`,
      );
    }

    if (result.status === "completed") {
      const parsed = resumeDataSchema.safeParse(result.data);
      if (!parsed.success) {
        throw new ResumeParseError(
          t`Parse result was invalid. Please retry the import.`,
          "AI_PARSE_FAILED",
        );
      }
      if (result.truncated) options.onTruncated?.();
      return parsed.data;
    }

    if (result.status === "failed") {
      throw new ResumeParseError(result.error, result.code ?? "INTERNAL");
    }
    // status === "pending": loop continues
  }

  throw new ResumeParseError(t`Parsing timed out. Please try again.`, "INTERNAL");
}

/**
 * Upper bound for the in-browser PDF parse. pdf.js can hang rather than throw
 * (worker script blocked or failing to load, a huge image-heavy file), and a
 * hang never reaches the server fallback. Bumping it trades a longer wait
 * before the server parse starts against more headroom for slow devices.
 */
export const CLIENT_PARSE_TIMEOUT_MS = 20_000;

const CLIENT_PARSE_TIMEOUT = Symbol("client-parse-timeout");

/** Resolves with the client parse result, or `null` on timeout, throw or low confidence. */
const tryParseOnClient = async (file: File): Promise<ResumeData | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const { parsePdfClientSide } = await import("@reactive-resume/parser");
    const outcome = await Promise.race([
      parsePdfClientSide(file),
      new Promise<typeof CLIENT_PARSE_TIMEOUT>((resolve) => {
        timer = setTimeout(() => {
          resolve(CLIENT_PARSE_TIMEOUT);
        }, CLIENT_PARSE_TIMEOUT_MS);
      }),
    ]);
    return outcome === CLIENT_PARSE_TIMEOUT ? null : outcome;
  } catch {
    // Any extraction failure (bad PDF structure, worker load failure, memory)
    // is not the user's problem: the caller falls back to the server parse.
    return null;
  } finally {
    clearTimeout(timer);
  }
};

export type ParseResumeFileOptions = {
  /**
   * Skip the deterministic client-side PDF parse and go straight to the
   * server/Groq path. Used to retry a file whose client-parsed data the server
   * refused to import.
   */
  skipClientParse?: boolean;
  /**
   * The caller shows this import's errors inline, so the shared axios
   * interceptor must not also raise its global error toast.
   */
  suppressErrorToast?: boolean;
  /** See {@link PollParseOptions.onTruncated}. */
  onTruncated?: () => void;
};

export type ParsedResumeFile = {
  data: ResumeData;
  /** Which path produced `data`: the in-browser PDF parser or the server/Groq job. */
  source: "client" | "server";
};

export async function parseResumeFileWithSource(
  file: File,
  options: ParseResumeFileOptions = {},
): Promise<ParsedResumeFile> {
  // For PDFs: try deterministic client-side parse first.
  // Failure, timeout or low confidence falls through to the Groq server path.
  if (file.type === "application/pdf" && !options.skipClientParse) {
    const result = await tryParseOnClient(file);
    if (result) return { data: result, source: "client" };
  }

  // Server/Groq path
  const formData = new FormData();
  formData.append("file", file);

  let jobId: string;
  const uploadStartedAt = Date.now();

  try {
    // No explicit Content-Type — axios derives the multipart boundary itself.
    const uploadResponse = await axios.post<{ jobId: string }>(
      "/resume-parser/parse",
      formData,
      inlineErrors(options.suppressErrorToast),
    );
    jobId = uploadResponse.data.jobId;
  } catch (error) {
    if (statusOf(error) === HTTP_TOO_MANY_REQUESTS) {
      throw new ResumeParseError(
        uploadFailureMessage(error),
        "RATE_LIMITED",
        `upload_${classifyImportFailure(error)}`,
      );
    }
    if (statusOf(error) === HTTP_PAYLOAD_TOO_LARGE) {
      throw new ResumeParseError(
        uploadFailureMessage(error),
        "FILE_TOO_LARGE",
        `upload_${classifyImportFailure(error)}`,
      );
    }
    // Preserve typed, permanent file rejections. The UI supplies localized
    // guidance; server messages may be English regardless of the active locale.
    const code = serverCode(error);
    if (
      statusOf(error) === HTTP_UNPROCESSABLE &&
      (code === "EXTRACT_FAILED" || code === "INVALID_FILE_TYPE" || code === "FILE_TOO_LARGE")
    ) {
      throw new ResumeParseError(
        uploadFailureMessage(error),
        code,
        `upload_${classifyImportFailure(error)}`,
        serverReason(error),
      );
    }
    // Keep unrecognized upload failures in a stable category. The wizard
    // renders localized retry and paste guidance for this code.
    throw new ResumeParseError(
      uploadFailureMessage(error),
      "UPLOAD_FAILED",
      `upload_${classifyImportFailure(error)}`,
      isNoResponseError(error) ? networkFailureDetail(Date.now() - uploadStartedAt) : undefined,
    );
  }

  return { data: await pollParseJob(jobId, options), source: "server" };
}

export async function parseResumeFile(
  file: File,
  options: ParseResumeFileOptions = {},
): Promise<ResumeData> {
  const { data } = await parseResumeFileWithSource(file, options);
  return data;
}

/** HTTP status of a rejected axios call, if the server answered at all. */
export const httpStatusOf = statusOf;

/**
 * Import CV text the user pasted (for a file we cannot read: a scan or photo).
 * Goes through the same server queue, AI call and job polling as an uploaded
 * file. The server's 422 (text too short / too long) and 429 (daily limit) are
 * mapped to stable codes so the wizard can show localized, actionable copy
 * instead of the server's English text.
 */
export async function parseResumeText(
  text: string,
  options: Pick<PollParseOptions, "onTruncated"> = {},
): Promise<ResumeData> {
  let jobId: string;

  try {
    const response = await axios.post<{ jobId: string }>(
      "/resume-parser/parse-text",
      { text },
      inlineErrors(true),
    );
    jobId = response.data.jobId;
  } catch (error) {
    const reason = `paste_${classifyImportFailure(error)}`;
    const status = statusOf(error);
    const code = serverCode(error);
    if (status === HTTP_UNPROCESSABLE && (code === "EXTRACT_FAILED" || code === "TEXT_TOO_LONG")) {
      throw new ResumeParseError(uploadFailureMessage(error), code, reason);
    }
    if (status === HTTP_TOO_MANY_REQUESTS) {
      throw new ResumeParseError(uploadFailureMessage(error), "RATE_LIMITED", reason);
    }
    throw new ResumeParseError(uploadFailureMessage(error), "UPLOAD_FAILED", reason);
  }

  return pollParseJob(jobId, { suppressErrorToast: true, ...options });
}

export async function parseLinkedinUrl(
  url: string,
  options: Pick<PollParseOptions, "onTruncated"> = {},
): Promise<ResumeData> {
  let jobId: string;

  try {
    const uploadResponse = await axios.post<{ jobId: string }>("/resume-parser/parse-linkedin", {
      url,
    });
    jobId = uploadResponse.data.jobId;
  } catch (error) {
    throw new Error(serverMessage(error) ?? t`Failed to start LinkedIn import.`);
  }

  return pollParseJob(jobId, options);
}
